import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";

const INTERVAL_LABELS = {
  DAY: "Daily",
  WEEK: "Weekly",
  MONTH: "Monthly",
};

const INTERVAL_HINTS = {
  DAY: "Debit will be processed every day.",
  WEEK: "Debit will be processed once every week.",
  MONTH: "Debit will be processed once every month.",
};

export const getAvailableAutoDebitIntervals = (config) => {
  if (!config) return [];
  if (typeof config === "boolean") {
    return config ? ["DAY", "WEEK", "MONTH"] : [];
  }

  const intervals = [];
  if (config.DAY || config.day) intervals.push("DAY");
  if (config.WEEK || config.week) intervals.push("WEEK");
  if (config.MONTH || config.month) intervals.push("MONTH");
  return intervals;
};

const AutoDebitIntervalDialog = ({ open, onClose, config }) => {
  const intervals = useMemo(
    () => getAvailableAutoDebitIntervals(config),
    [config],
  );
  const [selected, setSelected] = useState("DAY");
  const { activeAppTheme } = useSelector((state) => state.dashboard || {});
  const theme = activeAppTheme || {};

  const themeColors = {
    bigContainer: theme.bigContainerGradientColors?.length
      ? `linear-gradient(135deg, ${theme.bigContainerGradientColors[0]}, ${theme.bigContainerGradientColors[1]})`
      : "#ffffff",
    scaffold: theme.scaffoldColor || "#ffffff",
    text: theme.textColor || "#000000",
    primary: theme.primaryColor || "#FFD700",
    svgColor: theme.svgColor || "#ffffff",
  };

  useEffect(() => {
    if (!open) return;
    setSelected(intervals.includes("DAY") ? "DAY" : intervals[0] || "DAY");
  }, [open, intervals]);

  return (
    <Dialog
      open={open}
      onClose={() => onClose(null)}
      PaperProps={{
        sx: {
          background: themeColors.bigContainer,
          color: themeColors.text,
          border: `1px solid ${themeColors.scaffold}`,
        },
      }}
    >
      <DialogTitle>Select Auto Debit Interval</DialogTitle>
      <DialogContent>
        <RadioGroup
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {intervals.map((interval) => (
            <FormControlLabel
              key={interval}
              value={interval}
              control={<Radio sx={{ color: themeColors.text }} />}
              label={
                <div>
                  <Typography sx={{ fontWeight: 600 }}>
                    {INTERVAL_LABELS[interval] || interval}
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {INTERVAL_HINTS[interval] ||
                      "Debit will be processed on the selected interval."}
                  </Typography>
                </div>
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(null)}
          sx={{ color: themeColors.text }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onClose(selected)}
          disabled={!intervals.length}
          sx={{
            background: themeColors.primary,
            color: themeColors.text,
            "&:hover": { background: themeColors.svgColor },
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoDebitIntervalDialog;
